import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Instagram, Facebook, Youtube } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { getTranslation } from '../utils/translations';

// TikTok icon as SVG component
const TikTokIcon = ({ size = 20, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, setLanguage } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: language === 'en' ? 'PRIVATE ADVISORY' :
            language === 'es' ? 'ASESORÍA PRIVADA' :
            language === 'pt' ? 'CONSULTORIA PRIVADA' :
            language === 'fr' ? 'CONSEIL PRIVÉ' :
            'CONSULENZA PRIVATA', path: '/services' },
    { name: getTranslation(language, 'nav.about'), path: '/about' },
    { name: language === 'en' ? 'REVS TALES' :
            language === 'es' ? 'REVS TALES' :
            language === 'pt' ? 'REVS TALES' :
            language === 'fr' ? 'REVS TALES' :
            'REVS TALES', path: '/blog' },
    { name: language === 'en' ? 'REVS COMMUNITY' :
            language === 'es' ? 'COMUNIDAD REVS' :
            language === 'pt' ? 'COMUNIDADE REVS' :
            language === 'fr' ? 'COMMUNAUTÉ REVS' :
            'COMUNITÀ REVS', path: '/community' },
  ];

  const socialLinks = [
    { name: 'Instagram', icon: Instagram, url: 'https://www.instagram.com/revsvibes?igsh=MTI4dzVkYnV2b2Zrbw==' },
    { name: 'Facebook', icon: Facebook, url: 'https://www.facebook.com/share/1C9pZRq1GF/' },
    { name: 'TikTok', icon: TikTokIcon, url: 'https://www.tiktok.com/@revsvibes?_r=1&_t=ZT-93iBvykKi7h' },
    { name: 'Youtube', icon: Youtube, url: 'https://youtube.com/@revsvibesmedia?si=mT0XI8MIBwkPErBL' },
  ];

  const languages = [
    { code: 'en', name: 'English', display: 'EN' },
    { code: 'es', name: 'Español', display: 'ES' },
    { code: 'pt', name: 'Português', display: 'PT' },
    { code: 'fr', name: 'Français', display: 'FR' },
    { code: 'it', name: 'Italiano', display: 'IT' },
  ];

  const currentLanguage = languages.find(l => l.code === language);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-md' : 'bg-black/20 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_luxury-motors-13/artifacts/o65j0g82_Revs%20Vibes%20logo.png"
              alt="Revs Vibes"
              className="h-12 w-12 object-contain"
            />
            <span className={`text-2xl font-bold tracking-wider transition-colors ${
              isScrolled ? 'text-black' : 'text-white'
            }`}>
              REVS VIBES
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium tracking-wide transition-colors hover:text-gray-400 ${
                  location.pathname === link.path
                    ? isScrolled
                      ? 'text-black border-b-2 border-black'
                      : 'text-white border-b-2 border-white'
                    : isScrolled
                    ? 'text-gray-700'
                    : 'text-white/80'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Social Media Links */}
            <div className={`flex items-center gap-3 pl-4 border-l ${isScrolled ? 'border-gray-300' : 'border-white/30'}`}>
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`transition-colors hover:opacity-70 ${
                      isScrolled ? 'text-gray-700' : 'text-white'
                    }`}
                    aria-label={social.name}
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>

            {/* Language Selector */}
            <div className="relative group">
              <button
                className={`text-sm font-medium tracking-wide transition-colors ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                {currentLanguage?.display}
              </button>
              <div className="absolute right-0 mt-2 w-32 bg-white shadow-lg rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            </div>

            <Link to="/contact">
              <Button
                className="bg-black text-white hover:bg-gray-800 transition-all duration-300 px-6 py-2 rounded-full"
              >
                {getTranslation(language, 'nav.inquire')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className={isScrolled ? 'text-black' : 'text-white'} size={24} />
            ) : (
              <Menu className={isScrolled ? 'text-black' : 'text-white'} size={24} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white shadow-lg">
          <div className="px-4 pt-2 pb-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="block py-3 text-gray-700 hover:text-black font-medium"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Mobile Social Links */}
            <div className="flex items-center gap-4 py-3 border-t border-gray-200">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-700 hover:text-black"
                    aria-label={social.name}
                  >
                    <Icon size={20} />
                  </a>
                );
              })}
            </div>

            {/* Mobile Language Selector */}
            <div className="py-3 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-2">Language</p>
              <div className="flex flex-wrap gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`px-3 py-1 text-sm rounded ${
                      language === lang.code
                        ? 'bg-black text-white'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {lang.display}
                  </button>
                ))}
              </div>
            </div>

            <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
              <Button className="w-full bg-black text-white hover:bg-gray-800 mt-2">
                {getTranslation(language, 'nav.inquire')}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
